import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Pencil, Trash2, Receipt, Upload, ExternalLink, X, Search, Eye, Printer, MessageCircle, Loader2, Wallet, CheckCircle2 } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from 'sonner';
import { DatePicker } from "@/components/ui/date-filter";
import AddAppointment from "@/components/appointment/AddAppointment";
import { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSearch } from "@/contexts/SearchContext";
import UpdateAppointment from "@/components/appointment/UpdateAppointment";
import { useAuth } from "@/contexts/AuthContext";
import ViewAppointmentLabtests from "@/components/lab/viewAppointmentLabtests";
import ViewBill from "@/components/billing/ViewBill";
import { appointmentApi } from "@/api/appointment";
import { prescriptionApi } from "@/api/prescription";
import { notificationApi } from "@/api/notification";
import { ApiResponse, Appointment, AppointmentAttachType, AppointmentStatus } from "@/types/types";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { formatIstCalendarDateForApi } from "@/utils/dateUtils";
import { useBookingHospitals } from "@/hooks/useBookingHospitals";
import OpdPayoutDialog from "@/components/appointment/OpdPayoutDialog";
import {
  canRecordOpdPayoutRole,
  getOpdPayoutExpense,
  isOpdPayoutEligible,
} from "@/lib/opdPayout";
import {
  formatOpdBookingModeLabel,
  formatOpdConsultationTierLabel,
  hasOpdBookingChannelFields,
} from "@/lib/opdBookingDisplay";

export default function AppointmentManagement() {
  const location = useLocation();
  const navigate = useNavigate();
  const [filterDate, setFilterDate] = useState<Date>(new Date());
  const [listView, setListView] = useState<"day" | "upcoming">("upcoming");
  const [historySearch, setHistorySearch] = useState("");
  const [debouncedHistorySearch, setDebouncedHistorySearch] = useState("");
  const [isUpdateDialogOpen, setIsUpdateDialogOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
  const [viewBillAppointmentId, setViewBillAppointmentId] = useState<string | null>(null);
  const [rxDialogOpen, setRxDialogOpen] = useState(false);
  const [rxAppointmentId, setRxAppointmentId] = useState<string | null>(null);
  const [rxFile, setRxFile] = useState<File | null>(null);
  const [rxSendingId, setRxSendingId] = useState<string | null>(null);
  const [rxLoadingId, setRxLoadingId] = useState<string | null>(null);
  const [opdPayoutAppointment, setOpdPayoutAppointment] = useState<Appointment | null>(null);
  /** After CRM lead → patient registration, open Add Appointment for the new patient */
  const [addApptBootstrap, setAddApptBootstrap] = useState<{
    patientId: string;
    open: boolean;
    crmLeadId?: string;
    leadHospitalId?: string;
    registrationSource?: string | null;
    registrationSourceDetails?: string | null;
  } | null>(null);
  const queryClient = useQueryClient();
  const { searchQuery } = useSearch();
  const { user, isChainAdmin } = useAuth();

  useEffect(() => {
    const s = location.state as
      | {
          openAddAppointment?: boolean;
          patientId?: string;
          crmLeadId?: string;
          leadHospitalId?: string;
          registrationSource?: string | null;
          registrationSourceDetails?: string | null;
        }
      | undefined;
    if (s?.openAddAppointment && s.patientId) {
      setAddApptBootstrap({
        patientId: s.patientId,
        open: true,
        ...(s.crmLeadId ? { crmLeadId: s.crmLeadId } : {}),
        ...(s.leadHospitalId ? { leadHospitalId: s.leadHospitalId } : {}),
        registrationSource: s.registrationSource ?? null,
        registrationSourceDetails: s.registrationSourceDetails ?? null,
      });
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedHistorySearch(historySearch.trim()), 350);
    return () => clearTimeout(timer);
  }, [historySearch]);

  const effectiveSearch = useMemo(() => {
    const local = debouncedHistorySearch;
    const header = (searchQuery ?? "").trim();
    return local.length >= 2 ? local : header.length >= 2 ? header : "";
  }, [debouncedHistorySearch, searchQuery]);

  const isHistorySearch = effectiveSearch.length >= 2;

  const isSalesCrmRole =
    user?.role === 'SALES_PERSON' || user?.role === 'SALES_MANAGER';
  const showBookedByColumn = true;

  const appointmentBookedByLabel = (appointment: Appointment) =>
    appointment.bookedByLabel?.trim() ||
    appointment.creator?.name?.trim() ||
    (appointment.createdBy ? 'Staff' : 'Online / public');

  const OpdBookingCell = ({ appointment }: { appointment: Appointment }) => {
    if (!hasOpdBookingChannelFields(appointment)) {
      return <span className="text-muted-foreground">—</span>;
    }
    const mode = formatOpdBookingModeLabel(appointment.opdBookingMode);
    const tier = formatOpdConsultationTierLabel(appointment.opdConsultationTier);
    return (
      <div className="text-sm leading-snug">
        <div className="font-medium text-gray-800">{mode ?? "—"}</div>
        {tier ? (
          <div className="text-xs text-muted-foreground">{tier}</div>
        ) : null}
      </div>
    );
  };
  const { needsHospitalPicker } = useBookingHospitals();
  const showHospitalColumn =
    isChainAdmin || (isSalesCrmRole && needsHospitalPicker);
  const canViewBills =
    user?.role === 'RECEPTIONIST' ||
    user?.role === 'HOSPITAL_ADMIN' ||
    isSalesCrmRole;
  const canRecordOpdPayout = canRecordOpdPayoutRole(user?.role);
  const { data: appointments, isLoading: appointmentsLoading, isError: appointmentsError } = useQuery<ApiResponse<Appointment[]>>({
    queryKey: ["appointments", "day", formatIstCalendarDateForApi(filterDate), user?.role],
    enabled: !isHistorySearch && listView === "day",
    queryFn: async () => {
      const formattedDate = formatIstCalendarDateForApi(filterDate);
      const response = await appointmentApi.getAppointmentsByDate({ date: formattedDate });
      return response.data;
    },
    refetchOnWindowFocus: false,
  });

  const {
    data: upcomingAppointments,
    isLoading: upcomingLoading,
    isError: upcomingError,
  } = useQuery<ApiResponse<Appointment[]>>({
    queryKey: ["appointments", "upcoming"],
    enabled: !isHistorySearch && listView === "upcoming",
    queryFn: async () => {
      const response = await appointmentApi.getUpcomingAppointments();
      return response.data;
    },
    refetchOnWindowFocus: false,
  });

  const {
    data: historyAppointments,
    isLoading: historyLoading,
    isError: historyError,
  } = useQuery<ApiResponse<Appointment[]>>({
    queryKey: ["appointments-search", effectiveSearch, isSalesCrmRole],
    enabled: isHistorySearch,
    queryFn: async () => {
      const response = await appointmentApi.searchAppointments(effectiveSearch);
      return response.data;
    },
    refetchOnWindowFocus: false,
  });

  const cancelAppointmentMutation = useMutation({
    mutationFn: async (appointmentId: string) => {
      const response = await api.patch(`/api/appointment/update-status/${appointmentId}`, { status: 'CANCELLED' });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
      queryClient.invalidateQueries({ queryKey: ['appointments-search'] });
      toast.success('Appointment cancelled successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to cancel appointment');
    },
  });

  const filteredDayAppointments = appointments?.data?.filter((appointment: Appointment) => {
    if (!searchQuery || searchQuery === '') return true;
    const q = searchQuery.toLowerCase();
    return (
      appointment?.patient?.name?.toLowerCase().includes(q) ||
      appointment?.patient?.phone?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      appointment?.doctor?.name?.toLowerCase().includes(q)
    );
  });

  const filteredUpcomingAppointments = upcomingAppointments?.data?.filter((appointment: Appointment) => {
    if (!searchQuery || searchQuery === '') return true;
    const q = searchQuery.toLowerCase();
    return (
      appointment?.patient?.name?.toLowerCase().includes(q) ||
      appointment?.patient?.phone?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      appointment?.doctor?.name?.toLowerCase().includes(q)
    );
  });

  const displayAppointments = isHistorySearch
    ? historyAppointments?.data ?? []
    : listView === "upcoming"
      ? filteredUpcomingAppointments ?? []
      : filteredDayAppointments ?? [];

  const listLoading = isHistorySearch
    ? historyLoading
    : listView === "upcoming"
      ? upcomingLoading
      : appointmentsLoading;
  const listError = isHistorySearch
    ? historyError
    : listView === "upcoming"
      ? upcomingError
      : appointmentsError;

  // Prescription attachments (must be declared before any early returns to satisfy Rules of Hooks)
  const { data: rxAttachmentsResp, isLoading: loadingRxAttachments } = useQuery({
    queryKey: ["appointment-rx-attachments", rxAppointmentId],
    queryFn: async () => {
      if (!rxAppointmentId) return [];
      const res = await appointmentApi.getAttachments(rxAppointmentId);
      return (res.data as any)?.data ?? [];
    },
    enabled: rxDialogOpen && !!rxAppointmentId,
  });

  const rxAttachments = Array.isArray(rxAttachmentsResp) ? rxAttachmentsResp : [];
  const rxOnly = rxAttachments.filter((a: any) => a?.type === AppointmentAttachType.PRESCRIPTION);

  const uploadRxMut = useMutation({
    mutationFn: async () => {
      if (!rxAppointmentId) throw new Error("No appointment selected");
      if (!rxFile) throw new Error("Select a file");
      return appointmentApi.uploadAttachment({
        appointmentId: rxAppointmentId,
        type: AppointmentAttachType.PRESCRIPTION,
        file: rxFile,
      });
    },
    onSuccess: () => {
      toast.success("Prescription uploaded");
      setRxFile(null);
      queryClient.invalidateQueries({ queryKey: ["appointment-rx-attachments", rxAppointmentId] });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || err?.message || "Upload failed");
    },
  });

  const deleteRxMut = useMutation({
    mutationFn: async (attachmentId: string) => {
      return appointmentApi.deleteAttachment(attachmentId);
    },
    onSuccess: () => {
      toast.success("Deleted");
      queryClient.invalidateQueries({ queryKey: ["appointment-rx-attachments", rxAppointmentId] });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || err?.message || "Delete failed");
    },
  });

  if (listLoading) return <div>Loading...</div>;
  if (listError) return <div>Error loading data</div>;

  const handleEditAppointment = (appointment: Appointment) => {
    setSelectedAppointment(appointment);
    setIsUpdateDialogOpen(true);
  };

  const handleCancelAppointment = (appointmentId: string) => {
    if (window.confirm('Are you sure you want to cancel this appointment?')) {
      cancelAppointmentMutation.mutate(appointmentId);
    }
  };

  const handleDateChange = (date: Date | undefined) => {
    if (date) {
      setFilterDate(date);
      // Force refetch when date changes
      queryClient.invalidateQueries({ queryKey: ["appointments"] });
    }
  };

  const handleViewBill = (appointment: Appointment) => {
    if (appointment?.bills?.length) {
      setViewBillAppointmentId(appointment.bills[0].id);
    }
    else toast.message("No bill Created for this appointment");
  };

  const fetchPrescriptionPdfBlob = async (appointmentId: string): Promise<Blob | null> => {
    const summary = await prescriptionApi.getByAppointment(appointmentId);
    // Prefer OPD diagnosis/consultation PDF — it includes clinical notes, labs, and follow-up.
    // Fall back to pharmacy prescription PDF only when diagnosis record is missing.
    if (summary.hasDiagnosis) {
      const res = await api.get(prescriptionApi.diagnosisPdfUrl(appointmentId), {
        responseType: 'blob',
      });
      return res.data as Blob;
    }
    if (summary.hasPrescription && summary.prescriptionId) {
      const res = await api.get(prescriptionApi.openPdf(summary.prescriptionId), {
        responseType: 'blob',
      });
      return res.data as Blob;
    }
    return null;
  };

  const handleViewPrescription = async (appointmentId: string) => {
    setRxLoadingId(appointmentId);
    try {
      const blob = await fetchPrescriptionPdfBlob(appointmentId);
      if (!blob) {
        toast.error('No prescription available yet. Complete the consultation first.');
        return;
      }
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Could not open prescription');
    } finally {
      setRxLoadingId(null);
    }
  };

  const handlePrintPrescription = async (appointmentId: string) => {
    setRxLoadingId(appointmentId);
    try {
      const blob = await fetchPrescriptionPdfBlob(appointmentId);
      if (!blob) {
        toast.error('No prescription available yet. Complete the consultation first.');
        return;
      }
      const url = URL.createObjectURL(blob);
      const printWindow = window.open(url, '_blank');
      if (!printWindow) {
        toast.error('Please allow popups to print');
        URL.revokeObjectURL(url);
        return;
      }
      printWindow.onload = () => {
        printWindow.focus();
        printWindow.print();
      };
      setTimeout(() => URL.revokeObjectURL(url), 120_000);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Could not print prescription');
    } finally {
      setRxLoadingId(null);
    }
  };

  const handleSendPrescriptionWhatsApp = async (appointment: Appointment) => {
    if (!appointment.patient?.phone) {
      toast.error('Patient has no phone number on file');
      return;
    }
    setRxSendingId(appointment.id);
    try {
      await notificationApi.sendPrescriptionByAppointment(appointment.id);
      toast.success(`Prescription sent to ${appointment.patient.phone} on WhatsApp`);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to send prescription on WhatsApp');
    } finally {
      setRxSendingId(null);
    }
  };

  const showPrescriptionActions = (appointment: Appointment) =>
    appointment.status === AppointmentStatus.DIAGNOSED;

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="font-artis-serif text-2xl font-semibold text-artis-brown-dark">Appointments</h1>
        <AddAppointment
          key={addApptBootstrap ? `crm-${addApptBootstrap.patientId}` : "default-add-appt"}
          patientId={addApptBootstrap?.patientId ?? ""}
          initialOpen={addApptBootstrap?.open ?? false}
          crmOpdBookingLeadId={addApptBootstrap?.crmLeadId ?? null}
          leadHospitalId={addApptBootstrap?.leadHospitalId ?? null}
          onCrmOpdBookingComplete={() =>
            setAddApptBootstrap((prev) =>
              prev ? { ...prev, crmLeadId: undefined, leadHospitalId: undefined } : prev
            )
          }
          bookingHospitalId={user?.role === "SUPER_ADMIN" ? user.hospitalId : undefined}
          initialRegistrationSource={addApptBootstrap?.registrationSource}
          initialRegistrationSourceDetails={addApptBootstrap?.registrationSourceDetails}
          onDialogClose={() => setAddApptBootstrap(null)}
        />
      </div>

      {/* All Appointments Section */}
      <div>
        <div className="flex flex-col gap-3 mb-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="text-xl font-semibold text-gray-800">
              {isHistorySearch
                ? "Appointment history"
                : listView === "upcoming"
                  ? "Upcoming appointments"
                  : "All Appointments"}
            </div>
            {!isHistorySearch && listView === "upcoming" && (
              <p className="text-sm text-artis-slate mt-0.5">
                Today and all future scheduled visits (IST)
              </p>
            )}
            {isHistorySearch && (
              <p className="text-sm text-artis-slate mt-0.5">
                Showing past and upcoming visits matching &ldquo;{effectiveSearch}&rdquo;
              </p>
            )}
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <div className="relative w-full sm:w-72">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <Input
                type="search"
                placeholder="Search by patient name or phone…"
                value={historySearch}
                onChange={(e) => setHistorySearch(e.target.value)}
                className="pl-9"
              />
            </div>
            {!isHistorySearch && (
              <>
                <Tabs
                  value={listView}
                  onValueChange={(v) => setListView(v as "day" | "upcoming")}
                >
                  <TabsList>
                    <TabsTrigger value="upcoming">Upcoming</TabsTrigger>
                    <TabsTrigger value="day">By date</TabsTrigger>
                  </TabsList>
                </Tabs>
                {listView === "day" && (
                  <DatePicker
                    date={filterDate}
                    onDateChange={handleDateChange}
                  />
                )}
              </>
            )}
          </div>
        </div>
        <div className="rounded-lg border">
          <Table numberOfRows={11}>
            <TableHeader>
              <TableRow>
                <TableHead>Patient Name</TableHead>
                {showHospitalColumn && <TableHead>Hospital</TableHead>}
                <TableHead>Doctor Name</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Visit Id</TableHead>
                {showBookedByColumn && <TableHead>Booked by</TableHead>}
                <TableHead>OPD booking</TableHead>
                <TableHead>Scheduled At</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            {Array.isArray(displayAppointments) && displayAppointments.length > 0 ? (
              <TableBody>
                {displayAppointments.map((appointment) => (
                  <TableRow key={appointment.id}>
                    <TableCell className="font-medium">{appointment?.patient?.name}</TableCell>
                    {showHospitalColumn && (
                      <TableCell className="text-sm text-muted-foreground">
                        {appointment.hospital?.name ?? '—'}
                      </TableCell>
                    )}
                    <TableCell>{appointment?.doctor?.name}</TableCell>
                    <TableCell>{appointment?.patient?.phone}</TableCell>
                    <TableCell>{appointment?.visitId}</TableCell>
                    {showBookedByColumn && (
                      <TableCell className="text-sm text-muted-foreground">
                        {appointmentBookedByLabel(appointment)}
                      </TableCell>
                    )}
                    <TableCell>
                      <OpdBookingCell appointment={appointment} />
                    </TableCell>
                    <TableCell>
                      {new Date(appointment.scheduledAt).toLocaleString('en-GB', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                        timeZone: 'Asia/Kolkata'
                      })}
                    </TableCell>
                    <TableCell>
                      <span
                        className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${appointment.status === 'SCHEDULED'
                          ? 'bg-green-100 text-green-800'
                          : appointment.status === 'CANCELLED'
                            ? 'bg-red-100 text-red-800'
                            : appointment.status === 'CONFIRMED'
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}
                      >
                        {appointment.status}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleEditAppointment(appointment)}
                          className="p-1 hover:bg-gray-100 rounded-full"
                          title="Edit Appointment"
                        >
                          <Pencil className="w-4 h-4 text-gray-500" />
                        </button>
                        {canViewBills && (
                          <button
                            onClick={() => handleViewBill(appointment)}
                            className="p-1 hover:bg-gray-100 rounded-full"
                            title="View Bill"
                          >
                            <Receipt className="w-4 h-4 text-blue-500" />
                          </button>
                        )}
                        {canRecordOpdPayout && getOpdPayoutExpense(appointment) && (
                          <button
                            type="button"
                            className="p-1 rounded-full cursor-default"
                            title={`OPD payout recorded (${getOpdPayoutExpense(appointment)?.expenseNumber ?? 'saved'})`}
                          >
                            <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                          </button>
                        )}
                        {canRecordOpdPayout && isOpdPayoutEligible(appointment) && (
                          <button
                            type="button"
                            onClick={() => setOpdPayoutAppointment(appointment)}
                            className="p-1 hover:bg-gray-100 rounded-full"
                            title="Record OPD doctor payout"
                          >
                            <Wallet className="w-4 h-4 text-amber-600" />
                          </button>
                        )}
                        {appointment?.status === 'CONFIRMED' && (
                          <button
                            onClick={() => {
                              setRxAppointmentId(appointment.id);
                              setRxDialogOpen(true);
                            }}
                            className="p-1 hover:bg-gray-100 rounded-full"
                            title="Upload prescription"
                          >
                            <Upload className="w-4 h-4 text-emerald-600" />
                          </button>
                        )}
                        <ViewAppointmentLabtests appointmentId={appointment?.id} />
                        {showPrescriptionActions(appointment) && (
                          <>
                            <button
                              type="button"
                              onClick={() => handleViewPrescription(appointment.id)}
                              className="p-1 hover:bg-gray-100 rounded-full disabled:opacity-50"
                              title="View prescription"
                              disabled={rxLoadingId === appointment.id}
                            >
                              {rxLoadingId === appointment.id ? (
                                <Loader2 className="w-4 h-4 text-gray-500 animate-spin" />
                              ) : (
                                <Eye className="w-4 h-4 text-violet-600" />
                              )}
                            </button>
                            <button
                              type="button"
                              onClick={() => handlePrintPrescription(appointment.id)}
                              className="p-1 hover:bg-gray-100 rounded-full disabled:opacity-50"
                              title="Print prescription"
                              disabled={rxLoadingId === appointment.id}
                            >
                              <Printer className="w-4 h-4 text-slate-700" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleSendPrescriptionWhatsApp(appointment)}
                              className="p-1 hover:bg-gray-100 rounded-full disabled:opacity-50"
                              title="Send prescription on WhatsApp"
                              disabled={rxSendingId === appointment.id}
                            >
                              {rxSendingId === appointment.id ? (
                                <Loader2 className="w-4 h-4 text-green-600 animate-spin" />
                              ) : (
                                <MessageCircle className="w-4 h-4 text-green-600" />
                              )}
                            </button>
                          </>
                        )}
                        {appointment?.status !== 'DIAGNOSED' && appointment?.status !== 'CANCELLED' && (
                          <button
                            onClick={() => handleCancelAppointment(appointment.id)}
                            className="p-1 hover:bg-gray-100 rounded-full"
                            title="Cancel Appointment"
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </button>
                        )}


                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            ) : (
              <TableBody>
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-4 text-gray-500">
                    {isHistorySearch
                      ? 'No appointments found for this search'
                      : filterDate
                        ? 'No appointments found for the selected date'
                        : 'No appointments found'}
                  </TableCell>
                </TableRow>
              </TableBody>
            )}
          </Table>
        </div>
      </div>

      {selectedAppointment && (
        <UpdateAppointment
          appointment={selectedAppointment as Appointment}
          isOpen={isUpdateDialogOpen}
          onClose={() => {
            setIsUpdateDialogOpen(false);
            setSelectedAppointment(null);
          }}
        />
      )}

      {/* View Bill Dialog */}
      {viewBillAppointmentId && (
        <ViewBill
          billId={viewBillAppointmentId}
          isOpen={!!viewBillAppointmentId}
          onClose={() => setViewBillAppointmentId(null)}
        />
        // <ViewAppointmentBill
        //   appointmentId={viewBillAppointmentId}
        //   isOpen={!!viewBillAppointmentId}
        //   onClose={() => setViewBillAppointmentId(null)}
        //   ifpayment={()=>{}}
        // />
      )}

      <Dialog
        open={rxDialogOpen}
        onOpenChange={(v) => {
          setRxDialogOpen(v);
          if (!v) {
            setRxAppointmentId(null);
            setRxFile(null);
          }
        }}
      >
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Upload prescription</DialogTitle>
          </DialogHeader>

          <div className="space-y-3">
            <div>
              <Input
                type="file"
                accept="image/*,application/pdf"
                onChange={(e) => setRxFile(e.target.files?.[0] ?? null)}
                disabled={uploadRxMut.isPending}
              />
              <p className="text-xs text-muted-foreground mt-1">Accepted: PDF or image.</p>
            </div>

            <div className="rounded-md border">
              <div className="px-3 py-2 border-b text-sm font-medium">Existing prescriptions</div>
              <div className="max-h-56 overflow-auto">
                {loadingRxAttachments ? (
                  <div className="px-3 py-3 text-sm text-muted-foreground">Loading…</div>
                ) : rxOnly.length === 0 ? (
                  <div className="px-3 py-3 text-sm text-muted-foreground">No prescriptions uploaded.</div>
                ) : (
                  <ul className="divide-y">
                    {rxOnly.map((a: any) => (
                      <li key={a.id} className="flex items-center justify-between gap-2 px-3 py-2 text-sm">
                        <a
                          href={a.url}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 text-blue-600 hover:underline"
                        >
                          <ExternalLink className="h-4 w-4" />
                          View
                        </a>
                        <button
                          className="inline-flex items-center gap-1 text-red-600 hover:underline disabled:opacity-50"
                          disabled={deleteRxMut.isPending}
                          onClick={() => deleteRxMut.mutate(a.id)}
                          title="Delete"
                        >
                          <X className="h-4 w-4" />
                          Delete
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRxDialogOpen(false)}
              disabled={uploadRxMut.isPending}
            >
              Close
            </Button>
            <Button
              onClick={() => uploadRxMut.mutate()}
              disabled={!rxFile || uploadRxMut.isPending}
            >
              {uploadRxMut.isPending ? "Uploading…" : "Upload"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <OpdPayoutDialog
        appointment={opdPayoutAppointment}
        open={!!opdPayoutAppointment}
        onClose={() => setOpdPayoutAppointment(null)}
      />
    </div>
  );
}